<script setup lang="ts">
import { ref, computed, watch, nextTick, shallowRef, WatchEffect, watchEffect } from "vue";

import type { RecycleScroller } from "vue-virtual-scroller";

type TProductOption = {
    value: string; // JSON string of mastercodes: '["code1","code2"]'
    title: string; // human-friendly title
    codes: string[]; // parsed array of mastercodes
};
type TPreparedOption = TProductOption & { hay: string; codeCount: number };

const props = defineProps<{
    modelValue: string | null;
    extended: boolean;
    options: TProductOption[]; // All available products
    externalFilter?: string;
    duoOnly?: boolean; // only show products with 2 mastercodes
    placeHolder?: string;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: string | null): void;
    (e: "update:extended", value: boolean): void;
}>();

const open = ref(false);
const search = ref("");
const listRef = ref<InstanceType<typeof RecycleScroller> | null>(null);
const ITEM_SIZE = 36;

const norm = (s: string) => s.normalize("NFKD").trim().toLowerCase();
const tokenize = (s: string) => norm(s).split(/\s+/).filter(Boolean);

const prepared = shallowRef<TPreparedOption[]>([]);
watchEffect(() => {
    prepared.value = props.options.map((o) => {
        let codes = o.codes;
        if (!codes) {
            try {
                codes = JSON.parse(o.value);
            }
            catch {
                codes = [];
            }
        }
        return {
            ...o,
            hay: norm(`${ o.title } ${ codes.join(" ") }`),
            codeCount: codes.length || 1,
        };
    });
});

// OR (external filter), AND (internal search)
const filtered = computed(() => {
    const duo = !!props.duoOnly;
    const extTokens = tokenize(props.externalFilter ?? ""); // OR
    const intTokens = tokenize(search.value); // AND

    let arr = prepared.value;
    if (duo) { arr = arr.filter((o) => o.codeCount >= 2); }
    if (extTokens.length > 0) {
        arr = arr.filter((o) => extTokens.some((t) => o.hay.includes(t)));
    }
    if (intTokens.length > 0) {
        arr = arr.filter((o) => intTokens.every((t) => o.hay.includes(t)));
    }

    return arr;
});

function selectedCodeCount(): number {
    if (!props.modelValue) { return 0; }
    const found = prepared.value.find((o) => o.value === props.modelValue);
    if (found) {
        return found.codeCount;
    }
    try {
        return (JSON.parse(props.modelValue) as string[]).length;
    }
    catch {
        return 2;
    }
}
const canExtend = computed(() => selectedCodeCount() >= 2);

const selectedIndex = computed(() => props.modelValue ? filtered.value.findIndex((o) => o.value === props.modelValue) : -1);

// Scroll to selected item when opening
watch(open, async (isOpen) => {
    if (!isOpen) { return; }
    await nextTick(); // Wait for list to render
    const searchInput = document.getElementById("searchbar");
    if (searchInput) {
        searchInput.focus();
    }
    const i = selectedIndex.value;
    if (i < 0) { return; }
    try {
        const anyRef = listRef.value;
        if (anyRef && typeof anyRef.scrollToItem === "function") {
            anyRef.scrollToItem(i);
            return;
        }
        const el = listRef.value?.$el as HTMLElement | undefined;
        if (el) {
            el.scrollTop = i * ITEM_SIZE - el.clientHeight / 2 + ITEM_SIZE / 2;
        }
    }
    catch {
        // sometimes scrollToItem fails silently
    }
});

function onSelect(option: TProductOption | null) {
    emit("update:modelValue", option ? option.value : null);
    if (option && selectedCodeCount() >= 2) {
        emit("update:extended", false);
    }
    open.value = false;
}

function clear() {
    search.value = "";
}
</script>

<template>
    <div class="prod-select">
        <div class="select-row">
            <button
                type="button"
                class="trigger"
                @click="() => open = !open"
            >
                <span v-if="modelValue">
                    {{ (options.find(o => o.value === modelValue) || {}).title || 'Select...' }}
                </span>
                <span
                    v-else
                    class="placeholder"
                >{{ placeHolder || 'Select a product...' }}</span>
                <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    class="chev"
                ><path d="M6 9l6 6 6-6" /></svg>
            </button>

            <label
                v-if="canExtend"
                class="switch"
                title="Ext."
            >
                <input
                    type="checkbox"
                    :checked="!!extended"
                    aria-label="Ext."
                    @change="(e) => emit('update:extended', (e.target as HTMLInputElement).checked)"
                >
                <span class="slider" />
            </label>
            <div
                v-if="open"
                class="panel"
                @keydown.esc="() => open = false"
            >
                <input
                    id="searchbar"
                    v-model="search"
                    class="search"
                    type="text"
                    placeholder="Search Products..."
                    @keydown.stop
                >
                <RecycleScroller
                    ref="listRef"
                    :items="filtered"
                    :item-size="ITEM_SIZE"
                    key-field="value"
                    class="list"
                >
                    <template #default="{ item, index }">
                        <div
                            class="row"
                            :class="{ selected: item.value === modelValue }"
                            @click="() => onSelect(item)"
                        >
                            <span class="title">{{ item.title }}</span>
                            <span
                                v-if="item.codeCount > 1"
                                class="meta"
                            >({{ item.codes.length }} parts)</span>
                        </div>
                    </template>
                </RecycleScroller>

                <div
                    v-if="filtered.length === 0"
                    class="empty"
                >
                    No products found
                </div>

                <div class="footer">
                    <button
                        type="button"
                        class="clear"
                        @click="() => clear()"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        class="close"
                        @click="() => open = false"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.prod-select { position: relative; width: 100%; }
.select-row { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 8px; }

.trigger {
  width: 100%; text-align: left;
  padding: 6px 10px; border: 1px solid #d0d5dd; border-radius: 6px;
  background: #fff; cursor: pointer; min-height: 30px;
  display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 6px;
}
.trigger .placeholder { color: #8a8f98; }
.trigger .chev { width: 16px; height: 16px; fill: none; stroke: #667085; stroke-width: 2; }

.panel {
  position: absolute; z-index: 20; left: 0; right: 0; margin-top: 6px;
  background: #fff; border: 1px solid #d0d5dd; border-radius: 8px;
  box-shadow: 0 10px 20px rgba(0,0,0,.08);
  display: grid; grid-template-rows: auto 240px auto;
}
.search { margin: 8px; padding: 6px 8px; border:1px solid #d0d5dd; border-radius:6px; width: calc(100% - 36px);}
.list { overflow: auto; }
.row {
  height: 36px; display: grid; grid-template-columns: 1fr auto; align-items: center;
  padding: 0 10px; cursor: pointer;
}
.row:hover { background: #f7f8fb; }
.row.selected { background: #eef2ff; }
.title { font-size: 13px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; }
.meta { font-size: 11px; color: #667085; padding-left: 8px; }
.empty { padding: 10px; color: #8a8f98; }
.footer { display: flex; justify-content: space-between; padding: 6px 8px; border-top: 1px solid #eef0f3; }
.clear, .close {
  font-size: 22px; padding: 6px 40px; border: 4px solid #d0d5dd; border-radius: 6px; background: #f8fafc; cursor: pointer;
}
.clear:hover, .close:hover { background: #eef2ff; }

.switch { position: relative; display: inline-block; width: 42px; height: 22px; }
.switch input { opacity: 0; width: 0; height: 0; }
.switch .slider {
  position: absolute; cursor: pointer; inset: 0;
  background: #e5e7eb; border-radius: 34px; transition: background .2s ease;
  box-shadow: inset 0 0 0 1px #d1d5db;
}
.switch .slider:before {
  content: "";  position: absolute;  height: 18px; width: 18px;  left: 2px; top: 2px;  background: #fff;
  border-radius: 50%;  transition: transform .2s ease;  box-shadow: 0 1px 2px rgba(0,0,0,.2);
}
.switch input:checked + .slider {
  background: #4f46e5;
  box-shadow: inset 0 0 0 1px #4f46e5;
}
.switch input:checked + .slider::before {
  transform: translateX(20px);
}
</style>
